import * as React from "react"

import SimpleModal from "@/shadcn/components/SimpleModal"
import { ChangeSummary, type ChangeRow, type ChangeTotal } from "./ChangeSummary"
import { billDate, type ChangePreview, money, number, packs } from "./format"

/**
 * Confirms a change to a plan's add-on packs before it's made. Figma:
 * S311lGXE6JSyTnNAXHmtR7, section "Add-ons · confirm a stepper change" (30261:4556):
 * add social outputs 30261:4557, remove 30261:5228, add X posts 30261:5535, remove
 * 30261:5843.
 *
 * Adding shows what's due today and puts it on the button. Removing social packs
 * says the unused part is credited; removing X posts says the posts last until
 * renewal, since post allowances only shrink when the plan renews.
 */

export type AddOnCode = "add_on_social" | "add_on_posts_1"

type AddOnChangeDialogProps = {
  addOn: AddOnCode
  /** Pack counts before and after. */
  packs: { from: number; to: number }
  /** What the packs come to: social outputs, or X posts a month. */
  units: { from: number; to: number }
  /** The plan's recurring price before and after, per billing term. */
  prices: { from: number; to: number }
  annual: boolean
  /** The plan's next bill date, used until the preview gives one. */
  nextBilling: string
  preview: ChangePreview
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
  onClose: () => void
}

export default function AddOnChangeDialog({
  addOn,
  packs: packCount,
  units,
  prices,
  annual,
  nextBilling,
  preview,
  busy,
  onConfirm,
  onCancel,
  onClose,
}: AddOnChangeDialogProps) {
  const social = addOn === "add_on_social"
  const adding = packCount.to > packCount.from
  const changed = Math.abs(units.to - units.from)
  const removedPacks = packCount.from - packCount.to
  const ready = preview.status === "ready" ? preview : null
  const date = billDate(ready?.nextBillDate ?? nextBilling)
  const posts = (n: number) => (n ? `${number(n)} a month` : "0")

  const rows: ChangeRow[] = [
    social
      ? { label: "Social outputs", from: number(units.from), to: number(units.to) }
      : { label: "Extra X posts", from: posts(units.from), to: posts(units.to) },
    { label: annual ? "Annual price" : "Monthly price", from: money(prices.from), to: money(prices.to) },
  ]

  let title: string, description: string, note: string, cancelLabel: string, confirmLabel: string
  if (adding) {
    title = social ? `Add ${number(changed)} social outputs?` : `Add ${number(changed)} X posts a month?`
    description = social
      ? "Your plan updates as soon as you confirm."
      : "Extra posts are shared by all your X accounts and renew with your plan."
    note = `Your next bill is ${money(prices.to)} on ${date}.`
    cancelLabel = "Cancel"
    confirmLabel = ready && ready.dueToday > 0 ? `Confirm and pay ${money(ready.dueToday)}` : "Confirm"
  } else if (social) {
    title = `Remove ${number(changed)} social outputs?`
    description = `The change takes effect now. We'll credit the unused part of ${
      removedPacks === 1 ? "this pack" : "these packs"
    } to your next bill.`
    note = `Your next bill is ${money(prices.to)} on ${date}${ready && ready.credit > 0 ? ", minus your credit" : ""}.`
    cancelLabel = `Keep ${number(units.from)} outputs`
    confirmLabel = `Remove ${packs(removedPacks)}`
  } else {
    title = `Remove ${number(changed)} X posts a month?`
    description = `You'll keep your extra posts until your plan renews on ${date}.`
    note = `From ${date} your bill is ${money(prices.to)}.`
    cancelLabel = "Keep extra posts"
    confirmLabel = `Remove ${packs(removedPacks)}`
  }

  // Without a preview there's no amount to show; adding still says a charge is coming.
  const total: ChangeTotal | null =
    preview.status === "error"
      ? null
      : { label: "Due today", amount: ready ? money(ready.dueToday) : undefined, note }

  return (
    <SimpleModal
      title={title}
      description={description}
      cancelLabel={cancelLabel}
      confirmLabel={confirmLabel}
      confirmDisabled={preview.status === "loading"}
      busy={busy}
      busyLabel="Saving..."
      onConfirm={onConfirm}
      onCancel={onCancel}
      onClose={onClose}
    >
      <ChangeSummary rows={rows} total={total} />
      {preview.status === "error" && adding ? (
        <p className="tw:text-xs tw:text-muted-foreground">
          {social ? "You'll be charged a prorated amount today." : "You'll be charged for the extra posts today."}{" "}
          Your next bill is {money(prices.to)} on {date}.
        </p>
      ) : null}
    </SimpleModal>
  )
}
